// ─── Enums ────────────────────────────────────────────────────────────────────

export type UserRole =
  | 'ADMIN'
  | 'OPERATIONS'
  | 'PRODUCT_MANAGER'
  | 'SUPPORT'
  | 'SALES'
  | 'BILLING'
  | 'MARKETING';
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

/** Human-readable role names and what each role may actually do. */
export const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Administrator',
  OPERATIONS: 'Operations',
  PRODUCT_MANAGER: 'Produktmanagement',
  SUPPORT: 'Technischer Support',
  SALES: 'Vertrieb',
  BILLING: 'Buchhaltung',
  MARKETING: 'Marketing',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  ADMIN: 'Vollzugriff inkl. Benutzerverwaltung, API-Keys und Integrationen.',
  OPERATIONS: 'Server, Instanzen und Deployments verwalten.',
  PRODUCT_MANAGER: 'Produkte, Tarife, Compose-Templates und Releases pflegen.',
  SUPPORT: 'Alles einsehen; keine schreibenden Rechte.',
  SALES: 'Kunden und Bestellungen verwalten.',
  BILLING: 'Kundenstammdaten für die Abrechnung pflegen.',
  MARKETING:
    'Domains und Unternamen pflegen, Reichweite auswerten, Mailvorlagen gestalten, '
    + 'Kundenstammdaten pflegen. Kein Zugriff auf Server, Deployments, '
    + 'SSH-Schlüssel und Bereitschaft.',
};

export type CustomerStatus = 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
export type CustomerRole = 'ACCOUNT_OWNER' | 'BILLING_USER' | 'TECHNICAL_USER' | 'READ_ONLY';

export type ProductStatus = 'DRAFT' | 'ACTIVE' | 'DEPRECATED' | 'ARCHIVED';
export type PlanStatus = 'ACTIVE' | 'DEPRECATED' | 'ARCHIVED';
export type TemplateStatus = 'DRAFT' | 'ACTIVE' | 'DEPRECATED';

export type OrderType =
  | 'NEW_INSTANCE'
  | 'UPGRADE'
  | 'DOWNGRADE'
  | 'CANCELLATION'
  | 'ADDON'
  | 'MANUAL_ADJUSTMENT';

export type OrderStatus =
  | 'DRAFT'
  | 'PENDING_PAYMENT'
  | 'PAID'
  | 'PENDING_PROVISIONING'
  | 'PROVISIONING'
  | 'ACTIVE'
  | 'FAILED'
  | 'CANCELLED'
  | 'SCHEDULED'
  | 'COMPLETED';

export type InstanceStatus =
  | 'PENDING'
  | 'DEPLOYING'
  | 'ACTIVE'
  | 'UPGRADING'
  | 'DOWNGRADING'
  | 'SUSPENDED'
  /** Aufbewahrt, nicht mehr in Betrieb, ohne Rückkehr. */
  | 'ARCHIVED'
  | 'CANCELLATION_SCHEDULED'
  | 'TERMINATED'
  | 'DELETING'
  | 'DELETED'
  | 'FAILED';

export type ServerStatus =
  | 'PROVISIONING'
  | 'PREPARING'
  | 'READY'
  | 'ACTIVE'
  | 'DRAINING'
  | 'MAINTENANCE'
  | 'FULL'
  | 'FAILED'
  | 'DECOMMISSIONED';

export type JobType =
  | 'CREATE_SERVER'
  | 'PREPARE_SERVER'
  | 'CREATE_INSTANCE'
  | 'UPDATE_INSTANCE'
  | 'REDEPLOY_INSTANCE'
  | 'STOP_INSTANCE'
  | 'START_INSTANCE'
  | 'DELETE_INSTANCE'
  | 'BACKUP_INSTANCE'
  | 'EXPORT_INSTANCE'
  | 'COLLECT_METRICS'
  | 'SYNC_WOOCOMMERCE'
  | 'CLEANUP_ABANDONED_ORDERS';

export type JobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'RETRYING';

export type ReleaseStatus =
  | 'DRAFT'
  | 'TESTING'
  | 'APPROVED'
  | 'SCHEDULED'
  | 'ROLLING_OUT'
  | 'PARTIALLY_DEPLOYED'
  | 'DEPLOYED'
  | 'FAILED'
  | 'ROLLED_BACK'
  | 'DEPRECATED';

// ─── Entities ─────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /** Slack-Mitglieds-Kennung (U01ABC…) fuer Erwaehnungen bei Alarmen. */
  slackId: string | null;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  status: UserStatus;
  suspendedAt: string | null;
  suspendedReason: string | null;
  mustChangePassword: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  companyName: string;
  /**
   * Kurzform fuer technische Namen — "ancora" statt
   * "ancora Verlagsservice GmbH & Co. KG".
   *
   * Wird beim Anlegen einer Instanz fuer Subdomain und Stack-Name benutzt.
   */
  shortCode: string | null;
  billingEmail: string;
  technicalEmail: string | null;
  billingName: string | null;
  billingAddressLine1: string | null;
  billingAddressLine2: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingCountry: string | null;
  vatId: string | null;
  taxNumber: string | null;
  invoiceEmail: string | null;
  paymentTermsDays: number;
  customerReference: string | null;
  woocommerceCustomerId: number | null;
  status: CustomerStatus;
  createdAt: string;
  updatedAt: string;
  _count?: {
    orders: number;
    instances: number;
  };
}

export interface CustomerUser {
  id: string;
  customerId: string;
  email: string;
  name: string;
  role: CustomerRole;
  status: string;
  lastLoginAt: string | null;
  emailVerifiedAt: string | null;
  createdAt: string;
}

export const CUSTOMER_ROLE_LABELS: Record<CustomerRole, string> = {
  ACCOUNT_OWNER: 'Kontoinhaber',
  BILLING_USER: 'Buchhaltung',
  TECHNICAL_USER: 'Technischer Kontakt',
  READ_ONLY: 'Nur Lesen',
};

export type WooCommerceVisibility = 'PUBLISH' | 'DRAFT' | 'PRIVATE';

export interface ProductExtraAttribute {
  name: string;
  options: string[];
}

/**
 * Was ausgerollt wird — getrennt von dem, was verkauft wird (`Product`).
 * Siehe docs/KONZEPT-PRODUKT-UND-APPVORLAGE.md, Stufe 1 seit 10.10.2026.
 */
export interface Application {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  /** Server-IDs, auf denen Instanzen laufen dürfen; leer = alle. */
  erlaubteServer: string[];
  createdAt: string;
  updatedAt: string;
  products: Array<Pick<Product, 'id' | 'slug' | 'name' | 'status' | 'imVerkauf'>>;
  _count?: { templates: number; configFields: number; releases: number; instances: number };
}

/** Vorgaben eines Produkts an einer Application: für alle Pläne und je Plan (planId). */
export interface ProduktVorgaben {
  alle?: Record<string, string>;
  plaene?: Record<string, Record<string, string>>;
}

export interface ProductApplicationLink {
  productId: string;
  applicationId: string;
  sortOrder: number;
  vorgaben: ProduktVorgaben;
  application: Pick<Application, 'id' | 'slug' | 'name'> & {
    erlaubteServer?: string[];
    configFields?: ProductConfigField[];
    templates?: Array<{ id: string; version: string; status: string; createdAt: string }>;
  };
}

export interface ApplicationDetail extends Application {
  templates: Array<{ id: string; version: string; status: TemplateStatus; createdAt: string }>;
  configFields: ProductConfigField[];
  releases: Array<{ id: string; version: string; releaseChannel: string; status: string; createdAt: string }>;
  _count?: Application['_count'] & { instances: number };
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  extraAttributes: ProductExtraAttribute[];
  status: ProductStatus;
  /**
   * Steht dieses Produkt im Verkauf?
   *
   * `false` bei den Betriebsprodukten — WordPress, Magento, Moodle und dem
   * Sammelpunkt „Webseite". Sie brauchen den Vorlagenapparat, gehoeren aber in
   * kein Angebot. Siehe docs/KONZEPT-APPS-UND-WEBSEITEN.md.
   */
  imVerkauf: boolean;
  /** Was das Produkt verkauft: eine Application oder mehrere (Bündel). Nur in `GET /products/:id`. */
  applications?: ProductApplicationLink[];
  woocommerceProductId: number | null;
  wcVisibilityOverride: WooCommerceVisibility | null;
  wcSyncedAt: string | null;
  wcSyncError: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    plans: number;
    instances: number;
  };
}

export interface ProductPlan {
  id: string;
  productId: string;
  name: string;
  slug: string;
  monthlyPrice: string | null;
  yearlyPrice: string | null;
  woocommerceVariationIdMonthly: number | null;
  woocommerceVariationIdYearly: number | null;
  limits: Record<string, unknown>;
  features: Record<string, unknown>;
  resourceProfile: Record<string, unknown>;
  slotWeight: number;
  isUpgradeable: boolean;
  isDowngradeable: boolean;
  status: PlanStatus;
  createdAt: string;
  product?: Product;
}

export interface ProductConfigField {
  id: string;
  productId: string;
  key: string;
  label: string;
  type: string;
  required: boolean;
  defaultValue: string | null;
  validation: Record<string, unknown> | null;
  visibleInCC: boolean;
  visibleInPC: boolean;
  isSecret: boolean;
  deploymentRelevant: boolean;
  upgradeable: boolean;
  sortOrder: number;
}

export interface ComposeTemplate {
  id: string;
  productId: string;
  version: string;
  composeTemplate: string;
  envTemplate: string;
  /**
   * Was der PC beim Anlegen einer Instanz selbst erzeugt und verschlüsselt
   * ablegt. Fehlt es, bricht `${…:?required}` den Deploy ab — deshalb muss es
   * beim Anlegen einer neuen Vorlage mitgehen und nicht stillschweigend
   * verschwinden.
   */
  generatedSecrets: string[];
  adminEmailField: string | null;
  adminSecretKey: string | null;
  adminAppliesOnDeploy: boolean;
  customerAdminSupported: boolean;
  tenantsSupported: boolean;
  /** Woher sich die Mandanten einer laufenden Instanz lesen lassen. */
  tenantsSource: Record<string, unknown> | null;
  /**
   * Darf der PC in Instanzen dieser Vorlage Nutzer verwalten?
   *
   * Ein Merkmal der VORLAGE und nicht des Produkts: ob eine Instanz die noetigen
   * Endpunkte hat, haengt am ausgerollten Stand. Eine aeltere Instanz kann es
   * nicht, waehrend die Anwendung es laengst kann — und dann darf der Reiter
   * nicht erscheinen.
   *
   * Optional, weil aeltere Antworten des PC das Feld nicht tragen; `undefined`
   * ist dann wie `false`, und das ist die vorsichtige Richtung.
   */
  usersSupported?: boolean;
  reservedModules: string[];
  availableModules: string[];
  migrationStrategy: string | null;
  healthcheckConfig: Record<string, unknown> | null;
  backupConfig: Record<string, unknown> | null;
  status: TemplateStatus;
  createdAt: string;
}

export interface Order {
  id: string;
  customerId: string;
  productId: string | null;
  planId: string | null;
  type: OrderType;
  status: OrderStatus;
  requestedConfiguration: Record<string, unknown>;
  woocommerceOrderId: number | null;
  woocommerceSubscriptionId: number | null;
  paymentStatus: string | null;
  createdAt: string;
  paidAt: string | null;
  cancelledAt: string | null;
  customer?: Customer;
  plan?: ProductPlan;
}

export type InstanceEnvironment =
  | 'PRODUCTION'
  | 'STAGING'
  | 'TEST'
  | 'DEMO'
  | 'DEV';

export interface Instance {
  /**
   * Laufende Nummer, die ein Mensch vorlesen kann.
   *
   * Die `id` ist ein cuid und damit am Telefon unbrauchbar. Optional, weil
   * aeltere Antworten sie noch nicht tragen.
   */
  nummer?: number;
  /**
   * Produktinstanz, App oder Webseite.
   *
   * Bestimmt bisher nur, unter welchem Reiter die Instanz in der Liste steht.
   * Auf der Detailseite haengt seit der Reichweitenmessung auch ein Reiter
   * daran — siehe docs/KONZEPT-APPS-UND-WEBSEITEN.md.
   */
  art?: 'PRODUKT' | 'APP' | 'WEBSEITE';
  /**
   * Hat diese Instanz überhaupt eine Datenbank?
   *
   * Kommt aus dem Backend, das sie am Datenverzeichnis in der compose.yaml
   * erkennt — derselbe Weg, den die SQL-Konsole geht. Eine Webseite hat
   * meistens keine, und ein SQL-Reiter, der nur erklären kann, dass er nichts
   * kann, ist schlimmer als keiner.
   */
  hatDatenbank?: boolean;
  /** Nur bei `art = WEBSEITE`: die compose.yaml hängt an der Instanz. */
  composeInline?: string | null;
  /** Dasselbe für die .env. */
  envInline?: string | null;
  /** Ist die Reichweitenmessung dieser Webseite eingeschaltet? */
  statistik?: boolean;
  /** Zusaetzliche oeffentliche Adressen -- Kundenadresse, pgAdmin, weiteres. */
  weitereAdressen?: { id?: string; hostname: string; forwardHost?: string; notiz?: string | null }[];
  /** Freie Farbe fuer die Uebersicht, Hex oder null. */
  farbe?: string | null;
  /**
   * Produktion oder Abnahme. `null` heisst: nie festgelegt.
   *
   * Nullbar mit Absicht — bei Instanzen, die es vor diesem Feld gab, weiss es
   * niemand sicher. Sie pauschal als Produktion auszuweisen waere eine
   * Behauptung, und eine Abnahmeinstanz faelschlich als Produktion zu fuehren
   * ist genau die Angabe, auf die sich jemand im falschen Moment verlaesst.
   */
  environment: InstanceEnvironment | null;
  id: string;
  customerId: string;
  productId: string;
  /** Was hier läuft (Stufe 2). Bei Bündeln verschieden von Instanz zu Instanz desselben Produkts. */
  applicationId?: string | null;
  application?: { id: string; slug: string; name: string; configFields?: ProductConfigField[] } | null;
  planId: string | null;
  serverId: string | null;
  domain: string | null;
  subdomain: string | null;
  stackName: string | null;
  status: InstanceStatus;
  /** Wer den A-Record pflegt. */
  dnsPflege: 'PC' | 'BEIWAGEN' | 'MANUELL';
  currentTemplateId: string | null;
  /** Nur von GET /instances/:id mitgeliefert — für die Marke im Kopf. */
  currentRelease?: {
    id: string;
    version: string;
    releaseName: string | null;
    releaseChannel: string;
  } | null;
  currentConfig: Record<string, unknown>;
  releaseChannel: string;
  currentReleaseId: string | null;
  previousReleaseId: string | null;
  deployedAt: string | null;
  /** Laufzeiten und Dienste in den Containern, vom PC erfasst. */
  techStack?: { name: string; version: string; container: string }[] | null;
  techStackAt?: string | null;
  /** Nur in der Liste: der jüngste ändernde Eintrag im Audit-Log. */
  letzteAenderung?: { am: string; aktion: string; wer: string | null } | null;
  lastChangedAt: string | null;
  suspendedAt: string | null;
  terminationDate: string | null;
  deletionDate: string | null;
  createdAt: string;
  updatedAt: string;

  // Hosting mode and, for SELF_HOSTED, the call-home state.
  hostingMode: HostingMode;
  activationKey?: string | null;
  activationKeyUsedAt: string | null;
  agentTokenPrefix: string | null;
  agentTokenRotatedAt: string | null;
  lastHeartbeatAt: string | null;
  lastHeartbeatIp: string | null;
  reportedVersion: string | null;
  reportedConfigHash: string | null;
  hostFingerprint: string | null;
  agentVersion: string | null;
  activationState: ActivationState;
  activationValidUntil: string | null;
  activationGraceStartedAt: string | null;
  activationReason: string | null;

  customer?: Customer;
  product?: Product;
  plan?: ProductPlan;
  server?: Server;
  template?: ComposeTemplate;
}

export type HostingMode = 'MANAGED' | 'SELF_HOSTED' | 'OBSERVED';

export type ActivationState = 'ACTIVE' | 'PAYMENT_OVERDUE' | 'SUSPENDED' | 'TERMINATED';

/** What the instance is actually restricted to. Never harsher than ADMIN_ONLY. */
export type EnforcementLevel = 'NONE' | 'NOTICE' | 'READ_ONLY' | 'ADMIN_ONLY';

export type NoticeSeverity = 'INFO' | 'WARN' | 'BLOCKING';
export type NoticeAudience = 'ADMINS' | 'ALL';

export interface InstanceNotice {
  id: string;
  instanceId: string;
  severity: NoticeSeverity;
  audience: NoticeAudience;
  title: string;
  body: string;
  dismissible: boolean;
  activeFrom: string;
  activeUntil: string | null;
  automated: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InstanceHeartbeat {
  id: string;
  instanceId: string;
  receivedAt: string;
  version: string | null;
  agentVersion: string | null;
  configHash: string | null;
  ipAddress: string | null;
  metrics: Record<string, number>;
  reportedActivationState: string | null;
}

/** Row shape of the self-hosted overview, with values the API derives for us. */
export interface SelfHostedInstance {
  id: string;
  domain: string | null;
  status: InstanceStatus;
  activationState: ActivationState;
  activationValidUntil: string | null;
  activationGraceStartedAt: string | null;
  activationReason: string | null;
  lastHeartbeatAt: string | null;
  reportedVersion: string | null;
  agentVersion: string | null;
  activationKeyUsedAt: string | null;
  agentTokenPrefix: string | null;
  customer: { id: string; companyName: string };
  product: { slug: string; name: string };
  enforcement: { level: EnforcementLevel; graceDays: number };
  heartbeatAgeHours: number | null;
  _count: { notices: number };
}

export interface InstanceSecret {
  id: string;
  instanceId: string;
  key: string;
  version: number;
  rotatedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
}

export type ServerOwnership = 'SHARED' | 'DEDICATED_JUNI' | 'CUSTOMER_PROVIDED';

/** Zustand des hinterlegten SSH-Schluessels. Liefert `GET /servers` mit. */
export interface SshMeta {
  hasKey: boolean;
  keyType: string | null;
  hasPassphrase: boolean;
  version: number;
  updatedAt: string | null;
}

export interface Server {
  /** Freie Farbe fuer die Uebersicht, Hex oder null. */
  farbe?: string | null;
  /**
   * Kommt aus `GET /servers` mit, stand hier aber nicht -- die ServersPage
   * hat sich das lokal als `ServerWithSsh` dazudefiniert, und jede andere
   * Stelle war damit blind dafuer. Ein Typ, der weniger behauptet als die API
   * liefert, ist die harmlosere Haelfte des Problems; die andere ist, dass
   * man es an jeder neuen Stelle wieder selbst herausfinden muss.
   */
  ssh?: SshMeta;
  ownership: ServerOwnership;
  /** Set for DEDICATED_JUNI and CUSTOMER_PROVIDED, null for a shared pool machine. */
  customerId: string | null;
  providerAccountRef: string | null;
  customer?: { id: string; companyName: string } | null;
  id: string;
  provider: string;
  providerServerId: string | null;
  hostname: string;
  ipAddress: string;
  ipv6Address: string | null;
  region: string | null;
  serverType: string | null;
  cpuCores: number | null;
  ramMb: number | null;
  diskGb: number | null;
  /** Optional — die meisten Hosts haben keine Rechenkarte. */
  gpuModel: string | null;
  gpuVramMb: number | null;
  gpuCount: number | null;
  status: ServerStatus;
  capacityStatus: string;
  maxSlots: number;
  /**
   * Beide werden **nie geschrieben** und stehen immer auf 0.
   *
   * Sie stammen aus einer geplanten Belegungsrechnung, die es nie gab. Wer die
   * tatsaechliche Zahl braucht, nimmt `_count.instances` -- die kommt aus der
   * Datenbank und stimmt.
   *
   * Stehen gelassen, weil sie in der Datenbank existieren; entfernt gehoeren
   * sie zusammen mit dem Modell, nicht nebenbei.
   */
  reservedSlots: number;
  /** Siehe `reservedSlots` — immer 0. Nicht benutzen. */
  activeSlots: number;
  sshPort: number;
  sshUser: string;
  costPerMonth: string | null;
  notes: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  _count?: {
    instances: number;
  };
}

export interface DeploymentJob {
  id: string;
  instanceId: string | null;
  serverId: string | null;
  orderId: string | null;
  type: JobType;
  status: JobStatus;
  payload: Record<string, unknown>;
  logs: string | null;
  errorMessage: string | null;
  retryCount: number;
  maxRetries: number;
  bullJobId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  instance?: Instance;
  server?: Server;
  order?: Order;
}

export interface Release {
  id: string;
  /** Alt (vor Stufe 2); neue Releases gehören nur der Application. */
  productId: string | null;
  applicationId: string | null;
  version: string;
  releaseName: string | null;
  releaseChannel: string;
  backendImage: string | null;
  backendImageTag: string | null;
  frontendImage: string | null;
  frontendImageTag: string | null;
  workerImage: string | null;
  workerImageTag: string | null;
  databaseMigrationRequired: boolean;
  migrationStrategy: string;
  breakingChange: boolean;
  minTemplateVersion: string | null;
  targetTemplateVersion: string | null;
  releaseNotes: string | null;
  status: ReleaseStatus;
  createdBy: string | null;
  approvedBy: string | null;
  createdAt: string;
  approvedAt: string | null;
  rolledOutAt: string | null;
  product?: Product | null;
  application?: Pick<Application, 'id' | 'slug' | 'name'> | null;
}

export interface WebhookEvent {
  id: string;
  source: string;
  eventType: string;
  externalId: string | null;
  payload: Record<string, unknown>;
  status: string;
  processedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AuditLog {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ipAddress: string | null;
  automated: boolean;
  createdAt: string;
  user?: User;
}

// ─── API Response wrappers ─────────────────────────────────────────────────────

/**
 * Der Umschlag, den die Listen-Routen tatsaechlich senden.
 *
 * Vorher stand hier `{ data, total, page, pageSize }` -- eine Form, die es im
 * Backend nie gab. Der Typ hat den Fehler dadurch nicht nur verdeckt, sondern
 * abgesegnet: `data.total` liess sich schreiben und war zur Laufzeit
 * `undefined`, woraus das Audit-Log null Seiten errechnete und den
 * Weiter-Knopf fuer immer ausgraute. Ein erfundener Typ ist schlimmer als
 * keiner.
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ApiError {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

export interface EmbedKey {
  id: string;
  name: string;
  key: string;
  allowedOrigins: string[];
  rateLimit: number;
  productIds: string[];
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  _count?: { signups: number };
}

export interface PendingSignup {
  id: string;
  email: string;
  companyName: string;
  status: 'PENDING' | 'CONVERTED' | 'EXPIRED';
  billingCycle: string;
  sourceOrigin: string | null;
  convertedOrderId: string | null;
  createdAt: string;
  convertedAt: string | null;
}

export interface MfaStatus {
  enabled: boolean;
  enabledAt: string | null;
  backupCodesRemaining: number;
}

export interface MfaSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

/** Was die Zustandspruefung einer Instanz gefunden hat. */
export interface ZustandsBefund {
  ok: boolean;
  vorher: string;
  nachher: string;
  geaendert: boolean;
  container: {
    gesamt: number;
    laufend: number;
    ungesund: number;
    zeilen: { name: string; status: string }[];
  } | null;
  adressen: { adresse: string; code: number | null }[];
  meldung: string;
}
